import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const CHANGELOG = [
  {
    version: '2.0.0',
    date: 'March 28, 2026',
    type: 'major',
    title: 'Full Platform Rebuild',
    description: 'Complete rebuild on our own infrastructure. Faster, more reliable, fully self-hosted.',
    changes: [
      'Live APY data updated every 15 minutes across all 156 protocols',
      'New APY Leaderboard — highest yielding protocols ranked live',
      'Protocol search directly on homepage',
      'Compare up to 5 protocols side by side',
      'Top Movers section — biggest APY changes in 24h',
      'APY change email alerts for significant rate movements',
      '156 protocols across 17 categories',
      'Individual detail pages for every protocol',
    ]
  },
  {
    version: '1.5.0',
    date: 'March 22, 2026',
    type: 'feature',
    title: 'Staking Calculator Upgrade',
    description: 'Major upgrade with compound interest and multi-protocol comparison.',
    changes: [
      'Compare up to 5 protocols simultaneously',
      'Compound interest — daily, monthly, yearly, or none',
      'Time period selector — 1, 2, 3, 5, or 10 years',
      'Side-by-side comparison table',
    ]
  },
  {
    version: '1.4.0',
    date: 'March 20, 2026',
    type: 'feature',
    title: 'Blog & Education Expansion',
    description: 'New blog posts covering Ethereum staking, liquid staking, and crypto tax guides.',
    changes: [
      'New guide: How to Stake Ethereum (ETH) in 2026',
      'New guide: Liquid Staking vs Native Staking',
      'New guide: Crypto Staking Taxes for 2026',
      'Social share buttons on all blog posts',
    ]
  },
  {
    version: '1.3.0',
    date: 'March 15, 2026',
    type: 'feature',
    title: 'Newsletter & Alerts',
    description: 'Newsletter signup and APY change alerts.',
    changes: [
      'Newsletter signup on homepage, blog, and protocol pages',
      'Welcome email on signup',
      'APY change email alerts',
      'Uptime monitoring with instant alerts',
    ]
  },
  {
    version: '1.0.0',
    date: 'March 1, 2026',
    type: 'launch',
    title: 'ResidualVault Launch',
    description: 'Initial launch of the most comprehensive crypto staking comparison platform.',
    changes: [
      '111 staking protocols at launch',
      'Live APY comparison table',
      'Education Hub with DeFi courses',
      'Free staking rewards calculator',
    ]
  },
];

const typeColors: Record<string, string> = {
  major: 'bg-neon-red/20 text-neon-red border-neon-red/30',
  feature: 'bg-sky-blue/20 text-sky-blue border-sky-blue/30',
  launch: 'bg-green-400/20 text-green-400 border-green-400/30',
};

const typeLabels: Record<string, string> = {
  major: 'Major Release',
  feature: 'New Features',
  launch: 'Launch',
};

export default function ChangelogPage() {
  return (
    <div className="min-h-screen bg-navy-dark py-16">
      <SEO
        title="What's New — ResidualVault Changelog"
        description="Track the latest features, protocol additions, and improvements to ResidualVault. Updated regularly."
        canonical="https://www.residualvault.com/changelog"
        keywords="residualvault changelog, what's new, platform updates, new features"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4">
            <span className="text-cream">What's </span>
            <span className="gradient-text-neon">New</span>
          </h1>
          <p className="text-xl text-sky-blue max-w-2xl mx-auto">
            Every update, new feature, and improvement to ResidualVault.
          </p>
        </div>

        <div className="space-y-8">
          {CHANGELOG.map((entry) => (
            <div key={entry.version} className="card-premium">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sky-blue/60 text-sm font-mono">v{entry.version}</span>
                    <span className={`px-3 py-0.5 rounded-full text-xs font-bold border ${typeColors[entry.type]}`}>
                      {typeLabels[entry.type]}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-cream">{entry.title}</h2>
                </div>
                <span className="text-sky-blue/60 text-sm">{entry.date}</span>
              </div>
              <p className="text-sky-blue mb-4">{entry.description}</p>
              <ul className="space-y-2">
                {entry.changes.map((change, i) => (
                  <li key={i} className="flex items-start gap-3 text-sky-blue text-sm">
                    <span className="text-neon-red mt-0.5 shrink-0">→</span>
                    {change}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="card-premium text-center mt-16">
          <h2 className="text-2xl font-bold text-cream mb-3">Stay Updated</h2>
          <p className="text-sky-blue mb-6">Subscribe to get notified when we ship new features.</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/register" className="btn-neon inline-block">Create Free Account</Link>
            <Link to="/staking" className="bg-steel-blue/20 border border-sky-blue text-sky-blue font-semibold px-6 py-3 rounded-lg hover:bg-steel-blue/30 transition-all inline-block">Browse Protocols</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
